import { useContext, useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { DeviceType } from '../../../../../models/UserDevicesModels';
import { RootState } from '../../../../../stores/Store';
import UserDetailsContext from '../../../../../contexts/UserDetailsContext/UserDetailsContext';

import {
  EidLength,
  ImeiLength,
  NumberLettersRegex,
  NumbersLettersSpacesSymbolsRegex,
} from '../../../../../configs/RegexConfig';

import TextInput from '../../../../../components/TextInput/TextInput';
import Button from '../../../../../components/Button/Button';
import ButtonGroup from '../../../../../components/ButtonGroup/ButtonGroup';
import Dropdown from '../../../../../components/Dropdown/Dropdown';
import useAddDevice from './useAddDevice';

interface AddDeviceStepProps {
  onClose: () => void;
  onAddDeviceSuccess: () => void;
  unassignedReport?: boolean;
}
function AddDeviceStep({ onAddDeviceSuccess, onClose, unassignedReport }: AddDeviceStepProps) {
  const [t] = useTranslation();
  const user = useContext(UserDetailsContext);
  const { companyConfigs } = useSelector((state: RootState) => state.userDetailsReducer);
  const { isLoading, addDevice, companyOptions } = useAddDevice(onClose, onAddDeviceSuccess, unassignedReport);

  const deviceTypeOptions = Object.keys(DeviceType).map((dev) => ({
    key: dev,
    value: dev,
    label: t(`common.deviceType.${dev}`),
  }));

  const formik = useFormik({
    initialValues: {
      companyId: '',
      manufacturer: '',
      model: '',
      os: '',
      serialNumber: '',
      deviceType: '' as DeviceType,
      eid: '',
      imeiOne: '',
      imeiTwo: '',
      assetTagNumber: '',
      color: '',
      freeText: '',
    },
    validateOnMount: true,
    validateOnChange: true,
    enableReinitialize: true,
    validationSchema: yup.object({
      companyId: unassignedReport ? yup.string().required(t('common.inputValidations.requiredField')) : yup.string(),
      manufacturer: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .matches(
          NumbersLettersSpacesSymbolsRegex,
          t('common.inputValidations.onlyNumbersAndLettersAndSpacesAndSymbols'),
        ),
      model: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .matches(
          NumbersLettersSpacesSymbolsRegex,
          t('common.inputValidations.onlyNumbersAndLettersAndSpacesAndSymbols'),
        ),
      os: yup
        .string()
        .matches(
          NumbersLettersSpacesSymbolsRegex,
          t('common.inputValidations.onlyNumbersAndLettersAndSpacesAndSymbols'),
        ),
      serialNumber: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .matches(NumberLettersRegex, t('common.inputValidations.onlyNumbersAndLetters')),
      deviceType: yup.string().required(t('common.inputValidations.requiredField')),
      eid: yup
        .string()
        .max(EidLength, t('common.inputValidations.maxLength', { len: EidLength }))
        .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
      imeiOne: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .max(ImeiLength, t('common.inputValidations.maxLength', { len: ImeiLength }))
        .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
      imeiTwo: yup
        .string()
        .max(ImeiLength, t('common.inputValidations.maxLength', { len: ImeiLength }))
        .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
      freeText: yup
        .string()
        .max(
          companyConfigs.deviceFreeTextLimit,
          t('common.inputValidations.maxLength', { len: companyConfigs.deviceFreeTextLimit }),
        ),
      color: yup.string().max(100, t('common.inputValidations.maxLength', { len: 100 })),
      assetTagNumber: yup
        .string()
        .max(
          companyConfigs.deviceAssetTagNumberLimit,
          t('common.inputValidations.maxLength', { len: companyConfigs.deviceAssetTagNumberLimit }),
        ),
    }),
    onSubmit: (values) => {
      addDevice({
        ...values,
        companyId: unassignedReport ? values.companyId : undefined,
        userId: user?.id,
      });
    },
  });

  useEffect(() => {
    if (unassignedReport && companyOptions.length === 1 && !formik.values.companyId) {
      formik.setFieldValue('companyId', companyOptions[0].value as string);
    }
  }, [companyOptions]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Grid item xl={4} md={12} xs={12}>
        <form onSubmit={formik.handleSubmit}>
          {unassignedReport && (
            <Dropdown
              id="companyId"
              name="companyId"
              label={t('pages.addDeviceDrawer.company')}
              value={formik.values.companyId}
              onChange={(e) => formik.handleChange(e)}
              onBlur={formik.handleBlur}
              error={formik.touched.companyId && Boolean(formik.errors.companyId)}
              feedback={formik.touched.companyId && Boolean(formik.errors.companyId) ? formik.errors.companyId : ''}
              formStyle={{ paddingBottom: '1.5rem' }}
              items={companyOptions}
              disabled={companyOptions.length === 1}
              placeholder={
                t('common.placeholders.dropdownPlaceholder') + t('pages.addDeviceDrawer.company').toLowerCase()
              }
            />
          )}

          <TextInput
            autoComplete="off"
            key="manufacturer"
            id="manufacturer"
            name="manufacturer"
            type="text"
            label={t(`pages.addDeviceDrawer.manufacturer`)}
            value={formik.values.manufacturer}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.manufacturer && Boolean(formik.errors.manufacturer)}
            feedback={
              formik.touched.manufacturer && Boolean(formik.errors.manufacturer) ? formik.errors.manufacturer : ''
            }
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={
              t('common.placeholders.inputPlaceholder') + t(`pages.addDeviceDrawer.manufacturer`).toLowerCase()
            }
          />
          <TextInput
            autoComplete="off"
            key="model"
            id="model"
            name="model"
            type="text"
            label={t(`pages.addDeviceDrawer.model`)}
            value={formik.values.model}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.model && Boolean(formik.errors.model)}
            feedback={formik.touched.model && Boolean(formik.errors.model) ? formik.errors.model : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.addDeviceDrawer.model`).toLowerCase()}
          />

          <TextInput
            autoComplete="off"
            key="os"
            id="os"
            name="os"
            type="text"
            label={t(`pages.addDeviceDrawer.os`)}
            value={formik.values.os}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.os && Boolean(formik.errors.os)}
            feedback={formik.touched.os && Boolean(formik.errors.os) ? formik.errors.os : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.addDeviceDrawer.os`).toLowerCase()}
          />

          <TextInput
            autoComplete="off"
            key="serialNumber"
            id="serialNumber"
            name="serialNumber"
            type="text"
            label={t(`pages.addDeviceDrawer.serialNumber`)}
            value={formik.values.serialNumber}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.serialNumber && Boolean(formik.errors.serialNumber)}
            feedback={
              formik.touched.serialNumber && Boolean(formik.errors.serialNumber) ? formik.errors.serialNumber : ''
            }
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={
              t('common.placeholders.inputPlaceholder') + t(`pages.addDeviceDrawer.serialNumber`).toLowerCase()
            }
          />

          <Dropdown
            id="deviceType"
            name="deviceType"
            label={t('pages.addDeviceDrawer.deviceType')}
            value={formik.values.deviceType}
            onChange={(e) => formik.handleChange(e)}
            onBlur={formik.handleBlur}
            formStyle={{ paddingBottom: '1.5rem' }}
            items={deviceTypeOptions}
            placeholder={
              t('common.placeholders.dropdownPlaceholder') + t('pages.addDeviceDrawer.deviceType').toLowerCase()
            }
          />

          <TextInput
            autoComplete="off"
            key="eid"
            id="eid"
            name="eid"
            type="text"
            label={t(`pages.addDeviceDrawer.eid`)}
            value={formik.values.eid}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.eid && Boolean(formik.errors.eid)}
            feedback={formik.touched.eid && Boolean(formik.errors.eid) ? formik.errors.eid : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.addDeviceDrawer.eid`)}
          />

          <TextInput
            autoComplete="off"
            key="imeiOne"
            id="imeiOne"
            name="imeiOne"
            type="text"
            label={t(`pages.addDeviceDrawer.imeiOne`)}
            value={formik.values.imeiOne}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.imeiOne && Boolean(formik.errors.imeiOne)}
            feedback={formik.touched.imeiOne && Boolean(formik.errors.imeiOne) ? formik.errors.imeiOne : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.addDeviceDrawer.imeiOne`)}
          />

          <TextInput
            autoComplete="off"
            key="imeiTwo"
            id="imeiTwo"
            name="imeiTwo"
            type="text"
            label={t(`pages.addDeviceDrawer.imeiTwo`)}
            value={formik.values.imeiTwo}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.imeiTwo && Boolean(formik.errors.imeiTwo)}
            feedback={formik.touched.imeiTwo && Boolean(formik.errors.imeiTwo) ? formik.errors.imeiTwo : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.addDeviceDrawer.imeiTwo`)}
          />

          <TextInput
            autoComplete="off"
            key="assetTagNumber"
            id="assetTagNumber"
            name="assetTagNumber"
            type="text"
            label={t(`pages.addDeviceDrawer.assetTagNumber`)}
            value={formik.values.assetTagNumber}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.assetTagNumber && Boolean(formik.errors.assetTagNumber)}
            feedback={
              formik.touched.assetTagNumber && Boolean(formik.errors.assetTagNumber) ? formik.errors.assetTagNumber : ''
            }
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={
              t('common.placeholders.inputPlaceholder') + t(`pages.addDeviceDrawer.assetTagNumber`).toLowerCase()
            }
          />

          <TextInput
            autoComplete="off"
            key="color"
            id="color"
            name="color"
            type="text"
            label={t(`pages.addDeviceDrawer.color`)}
            value={formik.values.color}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.color && Boolean(formik.errors.color)}
            feedback={formik.touched.color && Boolean(formik.errors.color) ? formik.errors.color : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.addDeviceDrawer.color`).toLowerCase()}
          />

          <TextInput
            autoComplete="off"
            key="freeText"
            id="freeText"
            name="freeText"
            type="text"
            label={t(`pages.addDeviceDrawer.freeText`)}
            value={formik.values.freeText}
            onInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.freeText && Boolean(formik.errors.freeText)}
            feedback={formik.touched.freeText && Boolean(formik.errors.freeText) ? formik.errors.freeText : ''}
            formStyle={{ paddingBottom: '1.5rem' }}
            placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.addDeviceDrawer.freeText`).toLowerCase()}
            multiline
            inputStyle={{
              '& .MuiInputBase-input': {
                padding: '1rem',
              },
            }}
            minRows={3}
          />

          <ButtonGroup sx={{ paddingTop: '0.5rem' }}>
            <Button label={t('common.cancel')} color="alternative2Button" onClick={onClose} disabled={isLoading} />
            <Button
              label={t('pages.addDeviceDrawer.button')}
              color="primaryButton"
              type="submit"
              disabled={!formik.isValid || isLoading || sessionStorage.getItem('impersonateId') !== null}
            />
          </ButtonGroup>
        </form>
      </Grid>
    </Box>
  );
}

export default AddDeviceStep;
